import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { api, CustomFieldDefinition } from '../lib/api'
import TagsInput, { GRC_TAG_SUGGESTIONS } from '../components/TagsInput'
import CustomFieldsForm from '../components/CustomFieldsForm'
import { ArrowLeft, Save, Tag } from 'lucide-react'

interface Framework {
  id: string
  name: string
  display_name: string
}

interface UserOption {
  id: string
  username: string
}

export default function ControlNew() {
  const navigate = useNavigate()
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([])
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)
  const [form, setForm] = useState({
    framework_id: '',
    control_number: '',
    name: '',
    description: '',
    frequency: 'monthly',
    is_not_applicable: false,
    owner_id: '',
    tags: [] as string[],
    custom_fields: {} as Record<string, any>,
  })

  useEffect(() => {
    async function fetchFrameworks() {
      try {
        const res = await api.getFrameworks()
        if (res.success) {
          setFrameworks(res.frameworks)
          if (res.frameworks.length > 0) {
            setForm((f) => ({ ...f, framework_id: res.frameworks[0].id }))
          }
        }
      } catch (err) {
        console.error('Failed to load frameworks:', err)
      }
    }
    fetchFrameworks()
  }, [])

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await api.getUsers()
        if (res.success) {
          setUsers(res.users)
        }
      } catch (err) {
        console.error('Failed to load users:', err)
      }
    }
    fetchUsers()
  }, [])

  useEffect(() => {
    async function fetchCustomFieldDefinitions() {
      setLoadingCustomFields(true)
      try {
        const res = await api.getCustomFieldDefinitions('control')
        if (res.success) {
          setCustomFieldDefinitions(res.definitions)
        }
      } catch (err) {
        console.error('Failed to load custom field definitions:', err)
      } finally {
        setLoadingCustomFields(false)
      }
    }
    fetchCustomFieldDefinitions()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await api.createControl(form) as { success: boolean; control?: any; error?: string }
      if (res.success && res.control) {
        navigate(`/controls/${res.control.id}`)
      } else {
        setError(res.error || 'Failed to create control')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create control')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/controls" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Control</h1>
          <p className="text-gray-500">Create a new compliance control</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Framework *</label>
            <select
              value={form.framework_id}
              onChange={(e) => setForm({ ...form, framework_id: e.target.value })}
              className="input"
              required
            >
              <option value="">Select a framework</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.display_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Control Number *</label>
            <input
              type="text"
              value={form.control_number}
              onChange={(e) => setForm({ ...form, control_number: e.target.value })}
              className="input"
              placeholder="e.g., PCI-1.1.1"
              required
            />
          </div>
        </div>

        <div>
          <label className="label">Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input"
            placeholder="Control name"
            required
          />
        </div>

        <div>
          <label className="label">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input"
            rows={4}
            placeholder="Describe the control requirements..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Frequency *</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              className="input"
              required
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semi-annually">Semi-Annually</option>
              <option value="annually">Annually</option>
              <option value="none">None</option>
            </select>
          </div>

          <div>
            <label className="label">Not Applicable</label>
            <div className="flex items-center mt-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, is_not_applicable: !form.is_not_applicable })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  form.is_not_applicable ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    form.is_not_applicable ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="ml-3 text-sm text-gray-600">
                {form.is_not_applicable ? 'Control marked as N/A' : 'Control is applicable'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Mark as N/A if this control does not apply to your organization
            </p>
          </div>
        </div>

        <div>
          <label className="label flex items-center gap-1">
            <Tag className="h-4 w-4" />
            Tags
          </label>
          <TagsInput
            value={form.tags}
            onChange={(tags) => setForm({ ...form, tags })}
            placeholder="Add tags (e.g., access-control, encryption)..."
            suggestions={GRC_TAG_SUGGESTIONS}
          />
          <p className="text-xs text-gray-500 mt-1">
            Press Enter or comma to add a tag. Tags help categorize and filter controls.
          </p>
        </div>

        <div>
          <label className="label">Owner</label>
          <select
            value={form.owner_id}
            onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
            className="input"
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Fields */}
        <CustomFieldsForm
          fields={customFieldDefinitions}
          values={form.custom_fields}
          onChange={(fieldName, value) =>
            setForm({
              ...form,
              custom_fields: { ...form.custom_fields, [fieldName]: value },
            })
          }
          loading={loadingCustomFields}
        />

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Link to="/controls" className="btn-secondary">
            Cancel
          </Link>
          <button type="submit" className="btn-primary" disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Create Control'}
          </button>
        </div>
      </form>
    </div>
  )
}
